import { Injectable, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { DeepLinkService, LinkTab, LinkWindow, escapePart, linkStamp, parseLinkTime } from './deep-link.service';

/**
 * The Analyse board (a virtual dashboard): charts collected to look at them together over one time window.
 * A chart is named by a board chart id (see board/board-catalog.ts), e.g. "froeling.boiler",
 * "froeling.hc.Heizkreis 01", "thermo.knx-f470", "solar.battery", "wx.knxga-8-0-5", "valves.all".
 *
 * Two kinds of board:
 *
 * - SLOTS (admin session only): saved analyses on the server (/api/admin/analyses, X-Session-Token). Each
 *   holds title, note, charts, a window (a rolling preset or an absolute from / to), the legend selection
 *   per chart and the valve evaluation's order. Every edit is saved after a pause ({@link SAVE_PAUSE_MS});
 *   the URL names only the slot ("#analyse/<id>"), its state is on the server. The active slot (the one
 *   📌 in a chart popup adds to) is remembered per browser ({@link ACTIVE_KEY}). A 401 drops the session
 *   quietly (AppComponent's handler): the board then shows the guest note.
 * - SNAPSHOT links (everyone, read-only): "#analyse?c=<ids>&from=<s>&to=<s>&title=…&note=…
 *   &analyse.<chart id>.hide=…&analyse.valves.all.sort=…" — self-contained, absolute window, nothing
 *   stored; 🔗 and the PDF of a slot make one ({@link snapshotLink}). Links shared before the slots (same
 *   keys, also "r=<preset>" instead of from / to) open the same way. An admin can save one as a new slot.
 *
 * Without a session a bare "#analyse" or a slot link shows a note (never an error page); the Analyse tab
 * is shown only with a session or while the link is an analyse link (AppComponent).
 */

/** The rolling window presets (a slot's window can be one of these, re-evaluated against "now"). */
export const WINDOW_PRESETS = ['6h', '24h', '3d', '7d', '30d', '1y'] as const;
export type WindowPreset = typeof WINDOW_PRESETS[number];
export const PRESET_MS: Readonly<Record<WindowPreset, number>> = {
  '6h': 6 * 3600_000, '24h': 24 * 3600_000, '3d': 3 * 86_400_000, '7d': 7 * 86_400_000,
  '30d': 30 * 86_400_000, '1y': 365 * 86_400_000,
};

/** A slot's window: rolling (a preset ending now) or absolute (epoch ms). */
export type SlotWindow = { preset: WindowPreset } | { from: number; to: number };

/** A chart's series toggled away from their default visibility (dataset linkKeys). */
export interface ChartVis { hide: string[]; show: string[]; }

/** A saved analysis (GET /api/admin/analyses/<id>). */
export interface Analysis {
  id: string;
  slot: number;
  title: string;
  note: string;
  charts: string[];
  window: SlotWindow;
  vis: Record<string, ChartVis>;
  valvesSort: string | null;
  created: number;
  updated: number;
}

/** One entry of the slot list (GET /api/admin/analyses). */
export interface AnalysisSummary { id: string; slot: number; title: string; charts: number; updated: number; }

/** What the board shows: the active slot (editable) or a snapshot link (read-only). */
export interface BoardState {
  /** 'slot:<id>' or 'snap:<n>': a new key is another board (its undo history, its charts' legend state). */
  key: string;
  readOnly: boolean;
  title: string;
  note: string;
  charts: string[];
  window: SlotWindow;
  vis: Record<string, ChartVis>;
  valvesSort: string | null;
}

/**
 * What the Analyse page shows: loading (the slot list or a slot), a slot, a snapshot, the admin's
 * "no analyses yet", a guest's note, a slot id that does not exist, or a failed list load.
 */
export type BoardMode = 'loading' | 'slot' | 'snapshot' | 'noSlots' | 'guest' | 'missing' | 'failed';
export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/** Server limits (the API answers 400 / 409 beyond them; the board never asks for more). */
export const BOARD_LIMITS = { slots: 50, charts: 40, title: 120, note: 2000, visKeys: 40, visKey: 80, chartId: 160, sort: 40 } as const;
/** Characters a chart id may have on the server. */
const CHART_ID_RE = /^[A-Za-z0-9 ._:+\-äöüÄÖÜß()]+$/;

/** A save starts this long after the last edit. */
const SAVE_PAUSE_MS = 800;
/** localStorage: the active slot of this browser. */
const ACTIVE_KEY = 'boardActiveSlot';
const API = '/api/admin/analyses';
/** Link keys that make a bare "#analyse" a snapshot link. */
const SNAPSHOT_KEYS = ['c', 'from', 'to', 'r', 'title', 'note'];
/** The valve evaluation's chart id and its order key ("analyse.valves.all.sort"). */
export const VALVES_ALL = 'valves.all';

/** An answer of the analyses API other than 2xx (status 0: no answer). */
export class BoardApiError extends Error {
  constructor(readonly status: number, readonly detail = '') { super(`HTTP ${status}${detail ? ': ' + detail : ''}`); }
}

@Injectable({ providedIn: 'root' })
export class BoardService {
  /** Mode, slot list, active slot, snapshot or save state changed. */
  readonly update$ = new Subject<void>();

  mode: BoardMode = 'guest';
  /** The slot list, in slot order (admin; empty without a session). */
  slots: AnalysisSummary[] = [];
  /** The slot list is being loaded / could not be loaded (not a 401). */
  slotsLoading = false;
  slotsFailed = false;
  /** The active slot (full), once loaded. */
  active: Analysis | null = null;
  /** The last failed request (create, duplicate, delete, order, save): i18n key, cleared by the next success. */
  actionError: string | null = null;
  /** A create / duplicate / delete / order request is running. */
  busy = false;

  private auth: (() => Record<string, string>) | null = null;
  private onUnauthorized: () => void = () => {};
  private activeId: string | null = null;
  private activeLoading: string | null = null;
  private snapshot: BoardState | null = null;
  private snapKey = '';
  private snapSeq = 0;
  private routeKey = '';
  /**
   * Per slot: the newest version not sent yet (an edit; a failed save puts its version back). Saving is
   * kept per slot, so a switch of slot (chip, ＋, "Als Slot speichern", back / forward) never redirects or
   * drops an edit: only the edited slot's own version goes out.
   */
  private pending = new Map<string, Analysis>();
  /** Per slot: the PUT running and the version it sends (one at a time per slot). */
  private inflight = new Map<string, { sent: Analysis; done: Promise<void> }>();
  /** Per slot: the pause after its last edit. */
  private timers = new Map<string, any>();
  /** Per slot: its save state (the board shows the active slot's). */
  private states = new Map<string, SaveState>();
  /** Bumped with every change of the session: answers to an older session's saves are ignored. */
  private session = 0;
  private listSeq = 0;
  /** The last tab shown before the board (its "← Dashboard" goes back there). */
  private prevTab: LinkTab = 'energie';

  constructor(private readonly link: DeepLinkService, private readonly zone: NgZone, private readonly translate: TranslateService) {
    try { this.activeId = localStorage.getItem(ACTIVE_KEY); } catch { /* private mode */ }
    this.notePrevTab();
    this.route();
    // changed$ also fires outside Angular (a legend click elsewhere): route() acts only on a new tab / path / snapshot
    link.changed$.subscribe(() => { this.notePrevTab(); this.route(); });
    link.external$.subscribe(() => this.route());
    // pending saves go out before the page is left (keepalive: they outlive the page)
    window.addEventListener('pagehide', () => { for (const id of [...this.pending.keys()]) this.save(id, true); });
  }

  // ── session ─────────────────────────────────────────────────────────────────

  /**
   * The admin session (AppComponent, whenever its token changes): `headers` while a token is held, else null;
   * `unauthorized` drops a stale token (a 401 of the analyses API).
   */
  setAdminAuth(headers: (() => Record<string, string>) | null, unauthorized?: () => void): void {
    if (unauthorized) this.onUnauthorized = unauthorized;
    const was = !!this.auth;
    this.auth = headers;
    if (was === !!headers) return;
    this.session++;
    this.timers.forEach(t => clearTimeout(t));
    this.timers.clear();
    this.pending.clear();
    this.inflight.clear();
    this.states.clear();
    this.slots = [];
    this.active = null;
    this.activeLoading = null;
    this.actionError = null;
    this.slotsFailed = false;
    this.listSeq++;
    if (headers) this.loadSlots();
    this.route(true);
  }

  get admin(): boolean { return !!this.auth; }

  // ── what the board shows ────────────────────────────────────────────────────

  /** The board on the Analyse page (null: a note / loading instead). */
  get state(): BoardState | null {
    if (this.mode === 'snapshot') return this.snapshot;
    if (this.mode === 'slot' && this.active) return stateOf(this.active);
    return null;
  }

  get readOnly(): boolean { return this.mode !== 'slot'; }

  /** The charts of the board shown (a snapshot on the Analyse page, else the active slot's): the tab's badge. */
  get items(): string[] {
    if (this.mode === 'snapshot' && this.link.tab === 'analyse') return this.snapshot?.charts ?? [];
    return this.admin ? this.active?.charts ?? [] : [];
  }

  /** The slot id named by the link ("#analyse/<id>"), else null. */
  get linkedSlot(): string | null { return this.link.tab === 'analyse' ? this.link.childOf('analyse') : null; }

  get activeSlotId(): string | null { return this.mode === 'slot' ? this.active?.id ?? null : null; }

  /** The active slot's save state ("speichert…", "gespeichert ✓", the error with its retry). */
  get saveState(): SaveState {
    const id = this.active?.id;
    return id ? this.states.get(id) ?? 'idle' : 'idle';
  }

  /** The tab "← Dashboard" returns to. */
  get previousTab(): LinkTab { return this.prevTab; }

  // ── the active slot's charts (the 📌 buttons and the board's picker) ────────

  has(id: string): boolean { return !!this.active?.charts.includes(id); }

  /** 📌: the active slot can take a chart (a session; the slot list / active slot loaded). */
  get pinReady(): boolean { return this.admin && !this.slotsLoading && !this.busy && (!this.activeId || !!this.active); }

  /** The active slot's title for the 📌 tooltips ("Zu „<title>“ hinzufügen"). */
  get activeTitle(): string { return this.active ? this.active.title.trim() || this.t('BOARD_SLOT_UNTITLED') : ''; }

  /** The i18n key of a 📌 button's title (params: { title: activeTitle }). */
  pinKey(id: string): string {
    return !this.active ? 'BOARD_PIN_NEW' : this.has(id) ? 'BOARD_PIN_REMOVE' : 'BOARD_PIN_ADD';
  }

  /** 📌 / picker: add or remove a chart of the active slot; without one a new slot ("Neue Analyse") gets it. */
  toggle(id: string): void {
    if (!this.admin) return;
    if (!this.active) {
      if (this.pinReady && validChartId(id)) this.create({ charts: [id] }, false);
      return;
    }
    if (this.has(id)) this.remove(id); else this.add(id);
  }

  add(id: string): void {
    const a = this.active;
    if (!a || a.charts.includes(id) || a.charts.length >= BOARD_LIMITS.charts || !validChartId(id)) return;
    this.edit({ charts: [...a.charts, id] });
  }

  remove(id: string): void {
    const a = this.active;
    if (!a || !a.charts.includes(id)) return;
    const vis = { ...a.vis };
    delete vis[id];
    this.edit({ charts: a.charts.filter(x => x !== id), vis, ...(id === VALVES_ALL ? { valvesSort: null } : {}) });
  }

  /** Move a chart up (-1) or down (+1). */
  move(id: string, delta: number): void {
    const a = this.active;
    if (!a) return;
    const list = [...a.charts];
    const i = list.indexOf(id), j = i + delta;
    if (i < 0 || j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    this.edit({ charts: list });
  }

  // ── edits of the active slot (the board in slot mode) ──────────────────────

  setTitle(title: string): void { this.edit({ title: title.slice(0, BOARD_LIMITS.title) }); }

  setNote(note: string): void { this.edit({ note: note.slice(0, BOARD_LIMITS.note) }); }

  /** A preset (rolling) or an absolute window, the latter rounded outward to whole minutes. */
  setWindow(win: SlotWindow): void {
    const a = this.active;
    if (!a) return;
    const next = normWindow(win);
    if (!next || sameWindow(next, a.window)) return;
    this.edit({ window: next });
  }

  /** A legend click: the chart's series toggled away from their default (key → hidden). */
  setVis(chartId: string, toggles: Map<string, boolean>): void {
    const a = this.active;
    if (!a || !a.charts.includes(chartId)) return;
    const v: ChartVis = { hide: [], show: [] };
    toggles.forEach((hidden, key) => { if (key.length <= BOARD_LIMITS.visKey) (hidden ? v.hide : v.show).push(key); });
    v.hide = v.hide.slice(0, BOARD_LIMITS.visKeys);
    v.show = v.show.slice(0, BOARD_LIMITS.visKeys);
    const vis = { ...a.vis };
    if (v.hide.length || v.show.length) vis[chartId] = v; else delete vis[chartId];
    if (JSON.stringify(vis) === JSON.stringify(a.vis)) return;
    this.edit({ vis });
  }

  setValvesSort(sort: string | null): void {
    const a = this.active;
    if (!a) return;
    const next = sort && sort.length <= BOARD_LIMITS.sort ? sort : null;
    if (next !== a.valvesSort) this.edit({ valvesSort: next });
  }

  private edit(change: Partial<Analysis>): void {
    const a = this.active;
    if (!a) return;
    const next = this.active = { ...a, ...change };
    const sum = this.slots.find(s => s.id === a.id);
    if (sum) Object.assign(sum, { title: next.title, charts: next.charts.length });
    // this slot's version is what its save sends, whichever slot is active by then
    this.pending.set(a.id, next);
    if (this.states.get(a.id) !== 'error') this.states.set(a.id, 'saving');
    this.scheduleSave(a.id);
    this.update$.next();
  }

  // ── saving ─────────────────────────────────────────────────────────────────

  private scheduleSave(id: string): void {
    clearTimeout(this.timers.get(id));
    this.zone.run(() => this.timers.set(id, setTimeout(() => { this.timers.delete(id); this.save(id); }, SAVE_PAUSE_MS)));
  }

  /** "Erneut versuchen" after a failed save (every slot with an unsaved version). */
  retrySave(): void { this.flushSave(); }

  /**
   * PUT slot `id`'s pending version (last write wins). One PUT per slot at a time: a version edited
   * meanwhile goes out when it is done (right away once its pause is over or it was flushed). A failed
   * save keeps its version (unless a newer edit replaced it) for "Erneut versuchen" / the next edit.
   */
  private save(id: string, keepalive = false): Promise<void> {
    const running = this.inflight.get(id);
    const sent = this.pending.get(id);
    if (!sent) return running?.done ?? Promise.resolve();
    if (running && !keepalive) return running.done;   // its end sends the pending version
    this.pending.delete(id);
    this.states.set(id, 'saving');
    const session = this.session;
    const entry = { sent, done: Promise.resolve() };
    entry.done = (async () => {
      try {
        const r = normAnalysis(await this.api('PUT', '/' + encodeURIComponent(id), editable(sent), keepalive));
        if (session !== this.session) return;
        if (r && this.active?.id === id) this.active = { ...this.active, slot: r.slot, created: r.created, updated: r.updated };
        const sum = this.slots.find(s => s.id === id);
        if (r && sum) sum.updated = r.updated;
        this.states.set(id, this.pending.has(id) ? 'saving' : 'saved');
        this.actionError = null;
      } catch (e) {
        if (session !== this.session) return;   // a 401: the session (and its unsaved edits) is gone
        const status = e instanceof BoardApiError ? e.status : 0;
        if (status === 404) {
          // deleted elsewhere (another device): gone from the list; the page says so
          this.forget(id);
          this.slots = this.slots.filter(s => s.id !== id);
          if (this.active?.id === id) { this.active = null; this.setActiveId(null); }
          this.route(true);
        } else if (status === 401) {
          this.forget(id);
        } else {
          if (!this.pending.has(id)) this.pending.set(id, sent);
          this.states.set(id, 'error');
        }
      } finally {
        if (this.inflight.get(id) === entry) this.inflight.delete(id);
        if (session === this.session) {
          if (this.pending.has(id) && !this.timers.has(id) && this.states.get(id) !== 'error') this.save(id);
          this.update$.next();
        }
      }
    })();
    this.inflight.set(id, entry);
    this.update$.next();
    return entry.done;
  }

  /** Pending saves go out now (all slots, or slot `id`): before another slot becomes active, a copy, a retry. */
  private flushSave(id?: string): void {
    for (const k of id ? [id] : [...this.pending.keys()]) {
      clearTimeout(this.timers.get(k));
      this.timers.delete(k);
      this.save(k);
    }
  }

  /** Until slot `id`'s pending and running saves are done (a failed one is not retried; at most a few seconds). */
  private async settle(id: string): Promise<void> {
    const deadline = Date.now() + 6000;
    while (Date.now() < deadline) {
      const running = this.inflight.get(id);
      if (running) { await Promise.race([running.done, new Promise(r => setTimeout(r, Math.max(0, deadline - Date.now())))]); continue; }
      if (!this.pending.has(id) || this.states.get(id) === 'error') return;
      this.flushSave(id);
    }
  }

  /** Slot `id`'s unsaved version, pause and save state go (deleted / gone). */
  private forget(id: string): void {
    clearTimeout(this.timers.get(id));
    this.timers.delete(id);
    this.pending.delete(id);
    this.states.delete(id);
  }

  /** Another slot becomes active: an old "gespeichert ✓" is not shown again. */
  private dropSavedStates(): void {
    for (const [id, s] of this.states) if (s === 'saved') this.states.delete(id);
  }

  // ── the slot list ──────────────────────────────────────────────────────────

  /** GET the slot list, then the active slot (the remembered one, else the first). */
  loadSlots(): void {
    if (!this.admin) return;
    const seq = ++this.listSeq;
    this.slotsLoading = true;
    this.slotsFailed = false;
    this.route(true);
    this.api('GET', '')
      .then(list => {
        if (seq !== this.listSeq) return;
        this.slots = normList(list);
        this.slotsLoading = false;
        if (!this.slots.length && this.migrateLocalBoard()) return;
        const remembered = this.activeId && this.slots.some(s => s.id === this.activeId) ? this.activeId : null;
        const linked = this.linkedSlot;
        const id = linked && this.slots.some(s => s.id === linked) ? linked : remembered ?? this.slots[0]?.id ?? null;
        if (id) this.loadActive(id); else this.setActiveId(null);
        this.route(true);
      })
      .catch(e => {
        if (seq !== this.listSeq) return;
        this.slotsLoading = false;
        this.slotsFailed = !(e instanceof BoardApiError && e.status === 401);
        this.route(true);
      });
  }

  /**
   * The board collected in this browser before the slots existed (localStorage 'boardCharts', 2026-10-21):
   * becomes the admin's first slot, once; the old keys go. False when there is nothing to take over.
   */
  private migrateLocalBoard(): boolean {
    let charts: string[] = [];
    let meta: { title?: unknown; note?: unknown } = {};
    try {
      const v = JSON.parse(localStorage.getItem('boardCharts') ?? '[]');
      charts = Array.isArray(v) ? v.filter((x: unknown): x is string => typeof x === 'string' && validChartId(x)) : [];
      meta = JSON.parse(localStorage.getItem('boardMeta') ?? '{}') ?? {};
      localStorage.removeItem('boardCharts');
      localStorage.removeItem('boardMeta');
    } catch { return false; }
    if (!charts.length) return false;
    const title = typeof meta.title === 'string' && meta.title.trim() ? meta.title.trim().slice(0, 120) : this.t('TAB_ANALYSE');
    const note = typeof meta.note === 'string' ? meta.note.slice(0, 2000) : '';
    this.create({ title, note, charts: charts.slice(0, BOARD_LIMITS.charts) });
    return true;
  }

  /**
   * Make slot `id` the active one (remembered) and load it (unless loaded). A version of it not saved yet
   * (or being saved) is newer than the server's: that one is shown at once.
   */
  private loadActive(id: string): void {
    this.setActiveId(id);
    if (this.active?.id === id || this.activeLoading === id) return;
    this.flushSave();
    this.dropSavedStates();
    const local = this.pending.get(id) ?? this.inflight.get(id)?.sent;
    if (local) {
      this.active = local;
      this.activeLoading = null;
      this.update$.next();
      return;
    }
    this.active = null;
    this.activeLoading = id;
    this.update$.next();
    this.api('GET', '/' + encodeURIComponent(id))
      .then(r => {
        if (this.activeLoading !== id) return;
        this.activeLoading = null;
        const a = normAnalysis(r);
        if (a) this.active = a;
        this.route(true);
      })
      .catch(e => {
        if (this.activeLoading !== id) return;
        this.activeLoading = null;
        // gone meanwhile: the list again (a 401 has dropped the session already)
        if (e instanceof BoardApiError && e.status === 404) { this.slots = this.slots.filter(s => s.id !== id); this.setActiveId(null); }
        else if (!(e instanceof BoardApiError && e.status === 401)) this.slotsFailed = true;
        this.route(true);
      });
  }

  private setActiveId(id: string | null): void {
    this.activeId = id;
    try { if (id) localStorage.setItem(ACTIVE_KEY, id); else localStorage.removeItem(ACTIVE_KEY); } catch { /* private mode */ }
  }

  /** Show slot `id` ("#analyse/<id>"; a snapshot's keys go). */
  openSlot(id: string): void {
    this.link.clearPrefix('analyse.');
    this.link.openView('analyse/' + id);
  }

  /** ＋ / 📌 without a slot / "Als Slot speichern": a new slot from `init` (title "Neue Analyse" unless given). */
  async create(init: Partial<Analysis> = {}, open = true): Promise<void> {
    if (!this.admin || this.busy) return;
    if (this.slots.length >= BOARD_LIMITS.slots) { this.actionError = 'BOARD_SLOT_FULL'; this.update$.next(); return; }
    this.busy = true;
    this.actionError = null;
    this.update$.next();
    try {
      const body = { title: this.t('BOARD_SLOT_NEW_TITLE'), note: '', charts: [], window: { preset: '24h' }, vis: {}, valvesSort: null, ...init };
      const a = normAnalysis(await this.api('POST', '', body));
      if (!a) throw new BoardApiError(0, 'bad answer');
      this.adopt(a, open);
    } catch (e) {
      this.fail(e, 'BOARD_SLOT_CREATE_FAILED');
    } finally {
      this.busy = false;
      this.update$.next();
    }
  }

  /** "Als Slot speichern": the snapshot shown becomes a new slot (its absolute window, charts, legend, order, text). */
  saveSnapshot(): void {
    const s = this.snapshot;
    if (!s || this.mode !== 'snapshot') return;
    const charts = s.charts.filter(validChartId).slice(0, BOARD_LIMITS.charts);
    const vis: Record<string, ChartVis> = {};
    for (const id of charts) if (s.vis[id]) vis[id] = s.vis[id];
    this.create({
      title: s.title.slice(0, BOARD_LIMITS.title) || this.t('BOARD_SLOT_NEW_TITLE'),
      note: s.note.slice(0, BOARD_LIMITS.note),
      charts, window: s.window, vis,
      valvesSort: charts.includes(VALVES_ALL) ? s.valvesSort : null,
    });
  }

  async duplicate(id: string): Promise<void> {
    if (!this.admin || this.busy) return;
    if (this.slots.length >= BOARD_LIMITS.slots) { this.actionError = 'BOARD_SLOT_FULL'; this.update$.next(); return; }
    this.busy = true;
    this.actionError = null;
    this.update$.next();
    try {
      // the edits not saved yet go into the copy too
      await this.settle(id);
      const a = normAnalysis(await this.api('POST', '/' + encodeURIComponent(id) + '/duplicate'));
      if (!a) throw new BoardApiError(0, 'bad answer');
      this.adopt(a, true);
    } catch (e) {
      this.fail(e, 'BOARD_SLOT_DUPLICATE_FAILED');
    } finally {
      this.busy = false;
      this.update$.next();
    }
  }

  async delete(id: string): Promise<void> {
    if (!this.admin || this.busy) return;
    this.busy = true;
    this.actionError = null;
    this.update$.next();
    // its unsaved edits are not sent any more (back if the delete fails)
    const unsaved = this.pending.get(id);
    const session = this.session;
    this.forget(id);
    try {
      await this.api('DELETE', '/' + encodeURIComponent(id));
      const i = this.slots.findIndex(s => s.id === id);
      this.slots = this.slots.filter(s => s.id !== id);
      if (this.active?.id === id || this.activeId === id) {
        const next = this.slots[Math.min(Math.max(i, 0), this.slots.length - 1)]?.id ?? null;
        this.active = null;
        this.setActiveId(next);
        if (next) this.openSlot(next);
        else { this.link.openView('analyse'); this.route(true); }
      }
    } catch (e) {
      if (unsaved && session === this.session && !this.pending.has(id)) {
        this.pending.set(id, unsaved);
        this.states.set(id, 'saving');
        this.scheduleSave(id);
      }
      this.fail(e, 'BOARD_SLOT_DELETE_FAILED');
    } finally {
      this.busy = false;
      this.update$.next();
    }
  }

  /** Move a slot one place earlier (-1) or later (+1). */
  async moveSlot(id: string, delta: number): Promise<void> {
    if (!this.admin || this.busy) return;
    const list = [...this.slots];
    const i = list.findIndex(s => s.id === id), j = i + delta;
    if (i < 0 || j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    const before = this.slots;
    this.slots = list;
    this.busy = true;
    this.update$.next();
    try {
      const r = await this.api('PUT', '/order', list.map(s => s.id));
      // the new slot numbers: from the answer if it is the list, else from the list read again
      const fresh = normList(Array.isArray(r) ? r : await this.api('GET', ''));
      if (fresh.length) this.slots = fresh;
      this.actionError = null;
    } catch (e) {
      this.slots = before;
      this.fail(e, 'BOARD_SLOT_ORDER_FAILED');
    } finally {
      this.busy = false;
      this.update$.next();
    }
  }

  /** A slot created / duplicated: into the list, active, and shown (`open`). */
  private adopt(a: Analysis, open: boolean): void {
    this.flushSave();
    this.dropSavedStates();
    this.slots = [...this.slots.filter(s => s.id !== a.id), summaryOf(a)].sort((x, y) => x.slot - y.slot);
    this.active = a;
    this.activeLoading = null;
    this.setActiveId(a.id);
    if (open) this.openSlot(a.id);
    this.route(true);
  }

  private fail(e: unknown, key: string): void {
    if (e instanceof BoardApiError && e.status === 401) return;   // the session is gone: the guest note says enough
    this.actionError = e instanceof BoardApiError && e.status === 409 ? 'BOARD_SLOT_FULL' : key;
  }

  // ── snapshot links ─────────────────────────────────────────────────────────

  /**
   * The read-only link of the board shown over `win` (absolute, whole minutes): charts, window, title,
   * note, legend selection and the valve evaluation's order — nothing on the server, no slot reference.
   */
  snapshotLink(win: LinkWindow): string {
    const s = this.state;
    const params = new Map<string, string>();
    params.set('from', linkStamp(Math.floor(win.from / 60_000) * 60_000));
    params.set('to', linkStamp(Math.ceil(win.to / 60_000) * 60_000));
    if (s) {
      if (s.charts.length) params.set('c', s.charts.map(escapePart).join(','));
      if (s.title.trim()) params.set('title', escapePart(s.title.trim()));
      if (s.note.trim()) params.set('note', escapePart(s.note.trim()));
      for (const id of s.charts) {
        const v = s.vis[id];
        if (v?.hide.length) params.set(`analyse.${id}.hide`, v.hide.map(escapePart).join(','));
        if (v?.show.length) params.set(`analyse.${id}.show`, v.show.map(escapePart).join(','));
      }
      if (s.valvesSort && s.charts.includes(VALVES_ALL)) params.set(`analyse.${VALVES_ALL}.sort`, escapePart(s.valvesSort));
    }
    // in the order DeepLinkService writes them: from, to; c, note, title; then the charts' keys
    const rank = (k: string) => k === 'from' ? 0 : k === 'to' ? 1 : k.includes('.') ? 200 : 100;
    const keys = [...params.keys()].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
    const query = keys.map(k => escapePart(k) + '=' + params.get(k)).join('&');
    return location.origin + location.pathname + location.search + '#analyse?' + query;
  }

  /** A snapshot link's board (a relative "r=" or no window: resolved now, absolute from then on). */
  private parseSnapshot(): BoardState {
    const charts = this.link.getList('c').filter((x, i, all) => x && all.indexOf(x) === i);
    let from = parseLinkTime(this.link.get('from'));
    let to = parseLinkTime(this.link.get('to'));
    if (from == null || to == null || to <= from) {
      const span = PRESET_MS[this.link.get('r') as WindowPreset] ?? PRESET_MS['24h'];
      to = Math.ceil(Date.now() / 60_000) * 60_000;
      from = to - span;
    }
    const vis: Record<string, ChartVis> = {};
    for (const id of charts) {
      const m = this.link.visibility('analyse.' + id);
      if (!m.size) continue;
      const v: ChartVis = { hide: [], show: [] };
      m.forEach((hidden, key) => (hidden ? v.hide : v.show).push(key));
      vis[id] = v;
    }
    return {
      key: 'snap:' + (++this.snapSeq),
      readOnly: true,
      title: this.link.get('title') ?? '',
      note: this.link.get('note') ?? '',
      charts,
      window: { from, to },
      vis,
      valvesSort: this.link.get(`analyse.${VALVES_ALL}.sort`),
    };
  }

  /** The link's snapshot keys (one string: a change is another snapshot). */
  private snapshotKeys(): string {
    const parts: string[] = [];
    for (const k of SNAPSHOT_KEYS) { const v = this.link.get(k); if (v != null && v !== '') parts.push(k + '=' + v); }
    if (!parts.length) return '';
    // the charts' keys belong to it (only with one of the keys above: "analyse.*" alone is no snapshot)
    for (const id of this.link.getList('c')) {
      for (const n of ['hide', 'show', 'sort']) { const v = this.link.get(`analyse.${id}.${n}`); if (v) parts.push(`${id}.${n}=${v}`); }
    }
    return parts.join('&');
  }

  // ── routing: the link → the mode ───────────────────────────────────────────

  /**
   * Decide what the Analyse page shows from the link: "#analyse/<id>" a slot (admin), a link with
   * snapshot keys a read-only snapshot, a bare "#analyse" the active slot (admin: the URL becomes its
   * "#analyse/<id>") or a note. Only acts on a change (or `force`: the session / slot list / a slot changed).
   */
  private route(force = false): void {
    const onBoard = this.link.tab === 'analyse';
    const slot = onBoard ? this.link.childOf('analyse') : null;
    const snap = onBoard && !slot ? this.snapshotKeys() : '';
    const key = `${onBoard}|${slot ?? ''}|${snap}`;
    if (!force && key === this.routeKey) return;
    this.routeKey = key;
    const run = () => this.decide(onBoard, slot, snap);
    if (NgZone.isInAngularZone()) run(); else this.zone.run(run);
  }

  private decide(onBoard: boolean, slot: string | null, snap: string): void {
    let mode: BoardMode;
    if (!onBoard) {
      this.snapshot = null;
      this.snapKey = '';
      mode = this.admin ? 'slot' : 'guest';
    } else if (slot) {
      this.snapshot = null;
      this.snapKey = '';
      if (!this.admin) mode = 'guest';
      else if (this.slotsLoading) mode = 'loading';
      else if (this.slotsFailed) mode = 'failed';
      else if (!this.slots.some(s => s.id === slot)) mode = 'missing';
      else if (this.active?.id === slot) mode = 'slot';
      else { this.loadActive(slot); mode = this.active?.id === slot ? 'slot' : 'loading'; }
    } else if (snap) {
      if (snap !== this.snapKey || !this.snapshot) { this.snapshot = this.parseSnapshot(); this.snapKey = snap; }
      mode = 'snapshot';
    } else {
      this.snapshot = null;
      this.snapKey = '';
      if (!this.admin) mode = 'guest';
      else if (this.slotsLoading) mode = 'loading';
      else if (this.slotsFailed) mode = 'failed';
      else if (!this.slots.length) mode = 'noSlots';
      else {
        // the active slot (else the first) into the URL; changed$ routes again
        const id = this.activeId && this.slots.some(s => s.id === this.activeId) ? this.activeId : this.slots[0].id;
        this.mode = 'loading';
        this.update$.next();
        this.openSlot(id);
        return;
      }
    }
    this.mode = mode;
    this.update$.next();
  }

  private notePrevTab(): void {
    const tab = this.link.tab;
    if (tab && tab !== 'analyse') this.prevTab = tab;
  }

  // ── API ────────────────────────────────────────────────────────────────────

  /** One request to the analyses API with the session's headers; a 401 drops the session (quietly). */
  private async api(method: string, path: string, body?: unknown, keepalive = false): Promise<unknown> {
    const headers = this.auth?.();
    if (!headers) throw new BoardApiError(401);
    let r: Response;
    try {
      r = await fetch(API + path, {
        method, cache: 'no-store', keepalive,
        headers: { ...headers, ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch (e: any) {
      throw new BoardApiError(0, e?.message ?? String(e));
    }
    if (r.status === 401) {
      this.zone.run(() => this.onUnauthorized());
      throw new BoardApiError(401);
    }
    if (!r.ok) throw new BoardApiError(r.status, (await r.text().catch(() => '')).slice(0, 200));
    if (r.status === 204) return null;
    return r.json().catch(() => null);
  }

  private t(key: string): string { return this.translate.instant(key); }
}

// ── helpers ────────────────────────────────────────────────────────────────────

function validChartId(id: string): boolean {
  return !!id && id.length <= BOARD_LIMITS.chartId && CHART_ID_RE.test(id);
}

/** The editable fields of a slot (the PUT body). */
function editable(a: Analysis): Partial<Analysis> {
  return { title: a.title, note: a.note, charts: a.charts, window: a.window, vis: a.vis, valvesSort: a.valvesSort, updated: a.updated };
}

function stateOf(a: Analysis): BoardState {
  return { key: 'slot:' + a.id, readOnly: false, title: a.title, note: a.note, charts: a.charts, window: a.window, vis: a.vis, valvesSort: a.valvesSort };
}

function summaryOf(a: Analysis): AnalysisSummary {
  return { id: a.id, slot: a.slot, title: a.title, charts: a.charts.length, updated: a.updated };
}

/** A window as stored: a known preset, or an absolute one rounded outward to whole minutes; else null. */
export function normWindow(w: unknown): SlotWindow | null {
  if (!w || typeof w !== 'object') return null;
  const p = (w as any).preset;
  if (typeof p === 'string') return (WINDOW_PRESETS as readonly string[]).includes(p) ? { preset: p as WindowPreset } : null;
  const from = Number((w as any).from), to = Number((w as any).to);
  if (!isFinite(from) || !isFinite(to)) return null;
  const f = Math.floor(from / 60_000) * 60_000;
  let t = Math.ceil(to / 60_000) * 60_000;
  if (t <= f) t = f + 60_000;
  return { from: f, to: t };
}

export function sameWindow(a: SlotWindow, b: SlotWindow): boolean {
  if ('preset' in a || 'preset' in b) return 'preset' in a && 'preset' in b && a.preset === b.preset;
  return a.from === b.from && a.to === b.to;
}

/** The window a slot window shows at `now`: a preset ends at `now` (whole minutes). */
export function resolveWindow(w: SlotWindow, now = Date.now()): LinkWindow {
  if ('preset' in w) {
    const to = Math.ceil(now / 60_000) * 60_000;
    return { from: to - PRESET_MS[w.preset], to };
  }
  return { from: w.from, to: w.to };
}

const strs = (v: unknown): string[] => Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && !!x) : [];
const num = (v: unknown, d = 0): number => typeof v === 'number' && isFinite(v) ? v : d;

/** A full analysis from the API (tolerant: missing fields get their defaults), else null. */
function normAnalysis(v: unknown): Analysis | null {
  if (!v || typeof v !== 'object' || typeof (v as any).id !== 'string') return null;
  const o = v as any;
  const charts = strs(o.charts).filter((x, i, all) => all.indexOf(x) === i);
  const vis: Record<string, ChartVis> = {};
  if (o.vis && typeof o.vis === 'object') {
    for (const [id, x] of Object.entries(o.vis as Record<string, any>)) {
      if (!charts.includes(id) || !x || typeof x !== 'object') continue;
      const cv = { hide: strs(x.hide), show: strs(x.show) };
      if (cv.hide.length || cv.show.length) vis[id] = cv;
    }
  }
  return {
    id: o.id, slot: num(o.slot), title: typeof o.title === 'string' ? o.title : '', note: typeof o.note === 'string' ? o.note : '',
    charts, window: normWindow(o.window) ?? { preset: '24h' }, vis,
    valvesSort: typeof o.valvesSort === 'string' && o.valvesSort ? o.valvesSort : null,
    created: num(o.created), updated: num(o.updated),
  };
}

/** The slot list from the API, in slot order. */
function normList(v: unknown): AnalysisSummary[] {
  if (!Array.isArray(v)) return [];
  return v
    .filter(o => o && typeof o === 'object' && typeof o.id === 'string')
    .map(o => ({ id: o.id, slot: num(o.slot), title: typeof o.title === 'string' ? o.title : '', charts: num(o.charts), updated: num(o.updated) }))
    .sort((a, b) => a.slot - b.slot);
}
